/**
 * Local mock store for Course Category "New" badge admin UI.
 * Shape mirrors planned API (platform_folder_category + isNew).
 * Swap for api.courseCategoryAdminApi when backend lands — keep the same shape.
 */

const STORAGE_KEY = 'wm_admin_course_category_new_v1'
const STORAGE_VERSION = 1

const delay = (ms = 120) =>
	new Promise(resolve => {
		setTimeout(resolve, ms)
	})

const t = (en, vi = en) => ({
	en: { language: 'en', name: en },
	vi: { language: 'vi', name: vi },
})

/**
 * Seed mirrors production chip layout:
 * - Wellbeing (32) + Climate Change (34) start as isNew (current web hardcode)
 * - Other Business English / industry chips for realistic review
 */
const seedCategories = () => {
	const now = new Date().toISOString()
	return [
		{
			id: 1,
			parentId: null,
			translations: t('Business English', 'Tiếng Anh thương mại'),
			isNew: false,
			updatedAt: now,
			subCategories: [
				{
					id: 10,
					parentId: 1,
					translations: t('Finance & Banking', 'Tài chính & Ngân hàng'),
					isNew: false,
					updatedAt: now,
				},
				{
					id: 11,
					parentId: 1,
					translations: t('Hospitality', 'Khách sạn - Nhà hàng'),
					isNew: false,
					updatedAt: now,
				},
				{
					id: 12,
					parentId: 1,
					translations: t('Aviation', 'Hàng không'),
					isNew: false,
					updatedAt: now,
				},
				{
					id: 32,
					parentId: 1,
					translations: t(
						'Wellbeing and Emotional Intelligence',
						'Wellbeing and Emotional Intelligence'
					),
					isNew: true,
					updatedAt: now,
				},
				{
					id: 34,
					parentId: 1,
					translations: t(
						'Climate Change - YDCC 2025',
						'Climate Change - YDCC 2025'
					),
					isNew: true,
					updatedAt: now,
				},
				{
					id: 13,
					parentId: 1,
					translations: t('Sales', 'Bán hàng'),
					isNew: false,
					updatedAt: now,
				},
			],
		},
		{
			id: 2,
			parentId: null,
			translations: t('Industry English', 'Tiếng Anh chuyên ngành'),
			isNew: false,
			updatedAt: now,
			subCategories: [
				{
					id: 20,
					parentId: 2,
					translations: t('IT', 'CNTT'),
					isNew: false,
					updatedAt: now,
				},
				{
					id: 21,
					parentId: 2,
					translations: t('Healthcare', 'Y tế'),
					isNew: false,
					updatedAt: now,
				},
				{
					id: 22,
					parentId: 2,
					translations: t('Marketing', 'Marketing'),
					isNew: false,
					updatedAt: now,
				},
				{
					id: 23,
					parentId: 2,
					translations: t('Construction', 'Xây dựng'),
					isNew: false,
					updatedAt: now,
				},
				{
					id: 24,
					parentId: 2,
					translations: t('Beauty Industry', 'Làm đẹp'),
					isNew: false,
					updatedAt: now,
				},
			],
		},
	]
}

const clone = value => JSON.parse(JSON.stringify(value))

const readRaw = () => {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY)
		if (!raw) return null
		return JSON.parse(raw)
	} catch {
		return null
	}
}

const writeAll = roots => {
	window.localStorage.setItem(
		STORAGE_KEY,
		JSON.stringify({ version: STORAGE_VERSION, categories: roots })
	)
}

const readAll = () => {
	const parsed = readRaw()
	if (
		parsed &&
		parsed.version === STORAGE_VERSION &&
		Array.isArray(parsed.categories)
	) {
		return parsed.categories
	}
	const seeded = seedCategories()
	writeAll(seeded)
	return seeded
}

export const getCategoryDisplayName = (category, locale = 'en') => {
	if (!category?.translations) return '—'
	return (
		category.translations?.[locale]?.name ||
		category.translations?.en?.name ||
		category.translations?.vi?.name ||
		'—'
	)
}

/**
 * Client display order: isNew chips first (stable by id), then the rest (stable by id).
 * Web/mobile should use the same rule when backend lands.
 */
export const sortSubcategoriesForClient = (subCategories = []) => {
	const featured = []
	const rest = []
	for (const sub of subCategories) {
		if (sub?.isNew) featured.push(sub)
		else rest.push(sub)
	}
	featured.sort((a, b) => Number(a.id) - Number(b.id))
	rest.sort((a, b) => Number(a.id) - Number(b.id))
	return [...featured, ...rest]
}

const flattenLeaves = roots => {
	const parentsById = new Map()
	const leaves = []
	for (const root of roots) {
		parentsById.set(Number(root.id), root)
		for (const sub of root.subCategories || []) {
			leaves.push({
				...sub,
				parentNameEn: getCategoryDisplayName(root, 'en'),
				parentNameVi: getCategoryDisplayName(root, 'vi'),
			})
		}
	}
	return { leaves, parentsById, roots }
}

/**
 * @param {{
 *   q?: string,
 *   parentId?: number,
 *   isNew?: boolean,
 *   page?: number,
 *   pageSize?: number,
 * }} filters
 */
export async function listCourseCategoryLeaves(filters = {}) {
	await delay()
	const {
		q,
		parentId,
		isNew,
		page = 0,
		pageSize = 50,
	} = filters
	const query = String(q || '')
		.trim()
		.toLowerCase()

	const { leaves, roots } = flattenLeaves(readAll())
	let items = leaves.slice()

	if (parentId != null && parentId !== '') {
		items = items.filter(item => Number(item.parentId) === Number(parentId))
	}
	if (typeof isNew === 'boolean') {
		items = items.filter(item => Boolean(item.isNew) === isNew)
	}
	if (query) {
		items = items.filter(item => {
			const hay = [
				item.id,
				item.parentNameEn,
				item.parentNameVi,
				item.translations?.en?.name,
				item.translations?.vi?.name,
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()
			return hay.includes(query)
		})
	}

	// New first for admin scanning, then parent, then name
	items.sort((a, b) => {
		if (Boolean(a.isNew) !== Boolean(b.isNew)) {
			return a.isNew ? -1 : 1
		}
		const parentCmp = String(a.parentNameEn || '').localeCompare(
			String(b.parentNameEn || '')
		)
		if (parentCmp !== 0) return parentCmp
		return getCategoryDisplayName(a, 'en').localeCompare(
			getCategoryDisplayName(b, 'en')
		)
	})

	const total = items.length
	const newCount = leaves.filter(item => item.isNew).length
	const start = page * pageSize
	const data = items.slice(start, start + pageSize)

	const parents = roots.map(root => ({
		id: root.id,
		nameEn: getCategoryDisplayName(root, 'en'),
		nameVi: getCategoryDisplayName(root, 'vi'),
		subCount: (root.subCategories || []).length,
		newCount: (root.subCategories || []).filter(s => s.isNew).length,
	}))

	return {
		data,
		total,
		newCount,
		parents,
	}
}

export async function getParentChipPreview(parentId) {
	await delay(60)
	const roots = readAll()
	const parent = roots.find(root => Number(root.id) === Number(parentId))
	if (!parent) {
		const err = new Error('Parent category not found.')
		err.code = 'NOT_FOUND'
		throw err
	}
	const ordered = sortSubcategoriesForClient(parent.subCategories || [])
	return {
		parent: {
			id: parent.id,
			nameEn: getCategoryDisplayName(parent, 'en'),
			nameVi: getCategoryDisplayName(parent, 'vi'),
		},
		chips: ordered.map(sub => ({
			id: sub.id,
			nameEn: getCategoryDisplayName(sub, 'en'),
			nameVi: getCategoryDisplayName(sub, 'vi'),
			isNew: Boolean(sub.isNew),
		})),
	}
}

/**
 * Toggle / set isNew on a leaf category.
 * @param {number} id
 * @param {boolean} isNew
 */
export async function setCourseCategoryIsNew(id, isNew) {
	await delay(180)
	if (typeof isNew !== 'boolean') {
		const err = new Error('isNew must be a boolean.')
		err.code = 'VALIDATION'
		throw err
	}

	const roots = readAll()
	let found = false
	const next = roots.map(root => {
		const subs = (root.subCategories || []).map(sub => {
			if (Number(sub.id) !== Number(id)) return sub
			found = true
			return {
				...sub,
				isNew,
				updatedAt: new Date().toISOString(),
			}
		})
		return { ...root, subCategories: subs }
	})

	if (!found) {
		const err = new Error('Category not found.')
		err.code = 'NOT_FOUND'
		throw err
	}

	writeAll(next)
	const { leaves } = flattenLeaves(next)
	const updated = leaves.find(item => Number(item.id) === Number(id))
	return clone(updated)
}

/** Wipe local mock and reseed production-like defaults (for UI review). */
export async function resetCourseCategoryNewSeed() {
	await delay(80)
	window.localStorage.removeItem(STORAGE_KEY)
	const seeded = seedCategories()
	writeAll(seeded)
	return { ok: true, count: flattenLeaves(seeded).leaves.length }
}
